import React, { useState, useMemo } from 'react';
import {
  PlusIcon,
  PhoneIcon,
  EnvelopeIcon,
  PencilSquareIcon,
  TrashIcon,
  CheckIcon,
  ClockIcon,
  CalendarIcon,
  UserIcon,
  BuildingOffice2Icon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { useDeals } from '../../hooks/useDeals';
import { useCustomers } from '../../hooks/useCustomers';
import { Deal, DealFormData } from '../../types/deal';
import { Tooltip } from '../common/Tooltip';
import LoadingScreen from '../common/LoadingScreen';
import { formatDistanceToNow } from 'date-fns';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import DealForm from './DealForm';
import SearchBar from '../common/SearchBar';
import Modal from '../common/Modal';
import Pagination from '../common/Pagination';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';

interface DealListProps {
  customerId?: string;
  isFormOpen?: boolean;
  setIsFormOpen?: (open: boolean) => void;
}

const DealList: React.FC<DealListProps> = ({ 
  customerId, 
  isFormOpen: externalIsFormOpen, 
  setIsFormOpen: externalSetIsFormOpen 
}) => {
  const { deals, loading, addDeal, updateDeal, deleteDeal } = useDeals(customerId);
  const { customers, loading: customersLoading } = useCustomers();
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [internalIsFormOpen, setInternalIsFormOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewDeal, setViewDeal] = useState<Deal | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [dealToDelete, setDealToDelete] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Use external state if provided, otherwise use internal state
  const isFormOpen = externalIsFormOpen !== undefined ? externalIsFormOpen : internalIsFormOpen;
  const setIsFormOpen = externalSetIsFormOpen || setInternalIsFormOpen;

  const handleTypeFilter = (type: string) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleStatusFilter = (status: string) => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const getCustomerName = (id: string) => {
    const customer = customers?.find(c => c.id === id);
    if (!customer) return 'Unknown Customer';

    return customer.type === 'organization'
      ? customer.companyName
      : `${customer.firstName} ${customer.lastName}`;
  };

  const filteredDeals = useMemo(() => {
    return deals.filter((deal) => {
      const customerName = getCustomerName(deal.customerId) || '';
      const searchMatch = deal.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (deal.subject && deal.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
        customerName.toLowerCase().includes(searchTerm.toLowerCase());

      const typeMatch = selectedTypes.length === 0 || selectedTypes.includes(deal.type);
      const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(deal.status);

      return searchMatch && typeMatch && statusMatch;
    });
  }, [deals, searchTerm, selectedTypes, selectedStatuses, customers]);

  // Pagination logic
  const totalPages = Math.ceil(filteredDeals.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDeals = filteredDeals.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleSubmit = async (data: DealFormData) => {
    try {
      if (selectedDeal) {
        await updateDeal(selectedDeal.id, {
          ...data,
          date: new Date(data.date),
          followUpDate: data.followUpDate ? new Date(data.followUpDate) : undefined
        });
        toast.success(t('deals.messages.updated'));
      } else {
        await addDeal({
          ...data,
          date: new Date(data.date),
          followUpDate: data.followUpDate ? new Date(data.followUpDate) : undefined
        });
        toast.success(t('deals.messages.created'));
      }
      setIsFormOpen(false);
      setSelectedDeal(null);
    } catch (error) {
      console.error('Error submitting deal:', error);
      toast.error(t('deals.messages.error'));
    }
  };

  const handleDelete = async (id: string) => {
    setDealToDelete(id);
    setIsConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (dealToDelete) {
      try {
        await deleteDeal(dealToDelete);
        toast.success(t('deals.messages.deleted'));
      } catch (error) {
        console.error('Error deleting deal:', error);
        toast.error(t('deals.messages.error'));
      }
      setIsConfirmDeleteOpen(false);
      setDealToDelete(null);
    }
  };

  const handleView = (deal: Deal) => {
    setViewDeal(deal);
    setIsViewModalOpen(true);
  };

  if (loading || customersLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-4">
      {customerId && (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-800">
            {t('deals.customerDeals')}
          </h2>
          <button
            onClick={() => {
              setSelectedDeal(null);
              setIsFormOpen(true);
            }}
            className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-md text-sm flex items-center"
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            {t('deals.new')}
          </button>
        </div>
      )}

      <div className="w-full">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t('common.placeholders.deals')}
        />
      </div>

      {/* Type filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setSelectedTypes([])}
          className={`inline-flex items-center px-2.5 py-1.5 rounded-lg text-sm font-medium
            ${selectedTypes.length === 0
              ? 'bg-primary-600 text-white'
              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
        >
          {t('deals.fields.type')}
        </button>
        <button
          onClick={() => handleTypeFilter('phone')}
          className={`inline-flex items-center px-2.5 py-1.5 rounded-lg text-sm font-medium
            ${selectedTypes.includes('phone')
              ? 'bg-primary-600 text-white'
              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
        >
          <PhoneIcon className="w-4 h-4 mr-1.5" />
          <span>{t('deals.types.phone')}</span>
        </button>
        <button
          onClick={() => handleTypeFilter('email')}
          className={`inline-flex items-center px-2.5 py-1.5 rounded-lg text-sm font-medium
            ${selectedTypes.includes('email')
              ? 'bg-primary-600 text-white'
              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
        >
          <EnvelopeIcon className="w-4 h-4 mr-1.5" />
          <span>{t('deals.types.email')}</span>
        </button>
      </div>

      {/* Status filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setSelectedStatuses([])}
          className={`inline-flex items-center px-2.5 py-1.5 rounded-lg text-sm font-medium
            ${selectedStatuses.length === 0
              ? 'bg-primary-600 text-white'
              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
        >
          {t('deals.fields.status')}
        </button>
        <button
          onClick={() => handleStatusFilter('pending')}
          className={`inline-flex items-center px-2.5 py-1.5 rounded-lg text-sm font-medium
            ${selectedStatuses.includes('pending')
              ? 'bg-primary-600 text-white'
              : 'bg-white border border-gray-300 text-yellow-600 hover:bg-gray-50'
            }`}
        >
          <ClockIcon className="w-4 h-4 mr-1.5" />
          <span>{t('deals.status.pending')}</span>
        </button>
        <button
          onClick={() => handleStatusFilter('completed')}
          className={`inline-flex items-center px-2.5 py-1.5 rounded-lg text-sm font-medium
            ${selectedStatuses.includes('completed')
              ? 'bg-primary-600 text-white'
              : 'bg-white border border-gray-300 text-green-600 hover:bg-gray-50'
            }`}
        >
          <CheckIcon className="w-4 h-4 mr-1.5" />
          <span>{t('deals.status.completed')}</span>
        </button>
        <button
          onClick={() => handleStatusFilter('rejected')}
          className={`inline-flex items-center px-2.5 py-1.5 rounded-lg text-sm font-medium
            ${selectedStatuses.includes('rejected')
              ? 'bg-primary-600 text-white'
              : 'bg-white border border-gray-300 text-red-600 hover:bg-gray-50'
            }`}
        >
          <TrashIcon className="w-4 h-4 mr-1.5" />
          <span>{t('deals.status.rejected')}</span>
        </button>
      </div>

      {filteredDeals.length === 0 ? (
        <div className="bg-white p-6 rounded-lg text-center shadow-sm border border-gray-200">
          <p className="text-gray-500">{t('deals.noDeals')}</p>
        </div>
      ) : (
        <>
          {/* Desktop table view */}
          <div className="hidden md:block bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('deals.fields.type')}
                    </th>
                    {!customerId && (
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('deals.fields.customer')}
                      </th>
                    )}
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('deals.fields.description')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {t('deals.fields.date')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {t('deals.fields.followUpDate')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('deals.fields.status')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentDeals.map((deal) => {
                    const statusColor = deal.status === 'completed' ? 'text-green-600' :
                      deal.status === 'rejected' ? 'text-red-600' :
                        'text-yellow-600';
                    const StatusIcon = deal.status === 'completed' ? CheckIcon :
                      deal.status === 'rejected' ? TrashIcon :
                        ClockIcon;
                    const borderColor = deal.status === 'completed'
                      ? 'bg-green-400'
                      : deal.status === 'rejected'
                        ? 'bg-red-400'
                        : 'bg-yellow-400';

                    return (
                      <tr key={deal.id} className="hover:bg-gray-50">
                        <td className="pl-3 pr-3 py-3 relative">
                          <div className={`absolute left-0 top-0 bottom-0 w-1 ${borderColor}`}></div>
                          <div className="flex items-center ml-1">
                            {deal.type === 'phone' ? (
                              <div className="p-1.5 rounded bg-gradient-to-br from-blue-100 to-blue-200 flex-shrink-0">
                                <PhoneIcon className="h-4 w-4 text-blue-600" />
                              </div>
                            ) : (
                              <div className="p-1.5 rounded bg-gradient-to-br from-indigo-100 to-indigo-200 flex-shrink-0">
                                <EnvelopeIcon className="h-4 w-4 text-indigo-600" />
                              </div>
                            )}
                            <span className="text-sm text-gray-700 ml-2">
                              {t(`deals.types.${deal.type}`)}
                              </span>
                          </div>
                        </td>
                        {!customerId && (
                          <td className="px-3 py-3">
                            <div className="flex items-center">
                              {customers?.find(c => c.id === deal.customerId)?.type === 'organization' ? (
                                <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/10 text-blue-600 border border-blue-500/20 max-w-[150px] overflow-hidden">
                                  <BuildingOffice2Icon className="w-3 h-3 mr-1 flex-shrink-0" />
                                  <span className="truncate">{getCustomerName(deal.customerId)}</span>
                                </div>
                              ) : (
                                <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/10 text-green-600 border border-green-500/20 max-w-[150px] overflow-hidden">
                                  <UserIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                                  <span className="truncate">{getCustomerName(deal.customerId)}</span>
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                        <td className="px-3 py-3">
                          <div className="space-y-1 min-w-0">
                            {deal.type === 'email' && deal.subject && (
                              <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                                {deal.subject}
                              </p>
                            )}
                            <p className="text-sm text-gray-700 truncate max-w-xs">
                              {deal.description}
                            </p>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          <div className="flex items-center justify-center text-sm text-gray-700">
                            <CalendarIcon className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                            {new Date(deal.date).toLocaleDateString('hu-HU')}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          {deal.followUpDate ? (
                            <div className="flex items-center justify-center text-sm text-yellow-600">
                              <CalendarIcon className="h-3.5 w-3.5 mr-1.5" />
                              {new Date(deal.followUpDate).toLocaleDateString('hu-HU')}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${statusColor} bg-opacity-20 ${deal.status === 'completed' ? 'bg-green-100 border border-green-200' :
                            deal.status === 'rejected' ? 'bg-red-100 border border-red-200' :
                              'bg-yellow-100 border border-yellow-200'
                            }`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {t(`deals.status.${deal.status}`)}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex justify-end space-x-2">
                            <Tooltip content={t('common.actions.view')}>
                              <button
                                onClick={() => handleView(deal)}
                                className="text-gray-500 hover:text-blue-600"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                            </Tooltip>
                            <Tooltip content={t('common.actions.edit')}>
                              <button
                                onClick={() => {
                                  setSelectedDeal(deal);
                                  setIsFormOpen(true);
                                }}
                                className="text-gray-500 hover:text-primary-600"
                              >
                                <PencilSquareIcon className="h-4 w-4" />
                              </button>
                            </Tooltip>
                            <Tooltip content={t('common.actions.delete')}>
                              <button
                                onClick={() => handleDelete(deal.id)}
                                className="text-gray-500 hover:text-red-600"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Pagination integrated within the table container */}
            {filteredDeals.length > 0 && (
              <div className="border-t border-gray-200 p-3 bg-gray-50">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredDeals.length}
                  indexOfFirstItem={indexOfFirstItem}
                  indexOfLastItem={indexOfLastItem}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            )}
          </div>

          {/* Mobile view - updated with modern card design */}
          <div className="md:hidden space-y-4 mb-4">
            {filteredDeals.map(deal => {
              const statusColor = deal.status === 'completed' ? 'text-green-600' :
                deal.status === 'rejected' ? 'text-red-600' :
                  'text-yellow-600';
              const StatusIcon = deal.status === 'completed' ? CheckIcon :
                deal.status === 'rejected' ? TrashIcon :
                  ClockIcon;
              const borderColor = deal.status === 'completed'
                ? 'border-l-green-400'
                : deal.status === 'rejected'
                  ? 'border-l-red-400'
                  : 'border-l-yellow-400';

              return (
                <div key={deal.id} className={`bg-white rounded-lg p-4 shadow-sm border border-gray-200 border-l-4 ${borderColor}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      {deal.type === 'phone' ? (
                        <div className="flex items-center">
                          <div className="p-1.5 rounded bg-gradient-to-br from-blue-100 to-blue-200 mr-2">
                            <PhoneIcon className="h-4 w-4 text-blue-600" />
                          </div>
                          <span className="text-sm font-medium text-gray-700">{t('deals.types.phone')}</span>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <div className="p-1.5 rounded bg-gradient-to-br from-indigo-100 to-indigo-200 mr-2">
                            <EnvelopeIcon className="h-4 w-4 text-indigo-600" />
                          </div>
                          <span className="text-sm font-medium text-gray-700">{t('deals.types.email')}</span>
                        </div>
                      )}
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${statusColor} bg-opacity-20 ${deal.status === 'completed' ? 'bg-green-100 border border-green-200' :
                      deal.status === 'rejected' ? 'bg-red-100 border border-red-200' :
                        'bg-yellow-100 border border-yellow-200'
                      }`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {t(`deals.status.${deal.status}`)}
                    </span>
                  </div>

                  {/* Information section */}
                  <div className="mt-3">
                  {!customerId && (
                      <div className="mb-2">
                        <div className="flex items-center">
                      {customers?.find(c => c.id === deal.customerId)?.type === 'organization' ? (
                            <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/10 text-blue-600 border border-blue-500/20 max-w-full overflow-hidden">
                              <BuildingOffice2Icon className="w-3 h-3 mr-1 flex-shrink-0" />
                              <span className="truncate">{getCustomerName(deal.customerId)}</span>
                            </div>
                      ) : (
                            <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/10 text-green-600 border border-green-500/20 max-w-full overflow-hidden">
                              <UserIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                              <span className="truncate">{getCustomerName(deal.customerId)}</span>
                            </div>
                      )}
                        </div>
                    </div>
                  )}

                  {deal.type === 'email' && deal.subject && (
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        {deal.subject}
                      </p>
                  )}
                    <p className="text-sm text-gray-700">
                      {deal.description}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center justify-between text-xs text-gray-600 mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center mr-4 mb-2">
                      <CalendarIcon className="h-3 w-3 mr-1 text-gray-500" />
                      <span>{new Date(deal.date).toLocaleDateString('hu-HU')}</span>
                    </div>

                    {deal.followUpDate && (
                      <div className="flex items-center text-yellow-600 mr-4 mb-2">
                        <CalendarIcon className="h-3 w-3 mr-1" />
                        <span>{new Date(deal.followUpDate).toLocaleDateString('hu-HU')}</span>
                      </div>
                    )}

                    <div className="flex space-x-3 ml-auto mb-2">
                      <button
                        onClick={() => handleView(deal)}
                        className="text-gray-500 hover:text-blue-600 p-1"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedDeal(deal);
                          setIsFormOpen(true);
                        }}
                        className="text-gray-500 hover:text-primary-600 p-1"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(deal.id)}
                        className="text-gray-500 hover:text-red-600 p-1"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination for mobile */}
          {filteredDeals.length > 0 && (
            <div className="md:hidden mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={filteredDeals.length}
                indexOfFirstItem={indexOfFirstItem}
                indexOfLastItem={indexOfLastItem}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </>
      )}

      {/* Deal Form */}
      <DealForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedDeal(null);
        }}
        onSubmit={handleSubmit}
        initialData={selectedDeal || undefined}
        customerId={customerId}
      />

      {/* View Deal Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title={t('deals.view')}
        size="lg"
      >
        {viewDeal && (
          <div className="p-4 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Type */}
              <div>
                <h3 className="text-sm font-medium text-gray-600">{t('deals.fields.type')}</h3>
                <div className="mt-2 flex items-center">
                  {viewDeal.type === 'phone' ? (
                    <span className="flex items-center space-x-2 text-blue-600">
                      <PhoneIcon className="h-5 w-5" />
                      <span className="text-sm text-gray-700">{t('deals.types.phone')}</span>
                    </span>
                  ) : (
                    <span className="flex items-center space-x-2 text-indigo-600">
                      <EnvelopeIcon className="h-5 w-5" />
                      <span className="text-sm text-gray-700">{t('deals.types.email')}</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Customer */}
              <div>
                <h3 className="text-sm font-medium text-gray-600">{t('deals.fields.customer')}</h3>
                <div className="mt-2 flex items-center space-x-2">
                  {customers?.find(c => c.id === viewDeal.customerId)?.type === 'organization' ? (
                    <BuildingOffice2Icon className="h-4 w-4 text-gray-500" />
                  ) : (
                    <UserIcon className="h-4 w-4 text-gray-500" />
                  )}
                  <span className="text-gray-700">
                    {getCustomerName(viewDeal.customerId)}
                  </span>
                </div>
              </div>

              {/* Date */}
              <div>
                <h3 className="text-sm font-medium text-gray-600">{t('deals.fields.date')}</h3>
                <div className="mt-2">
                  <p className="text-gray-700">{new Date(viewDeal.date).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Follow Up Date */}
              <div>
                <h3 className="text-sm font-medium text-gray-600">{t('deals.fields.followUpDate')}</h3>
                <div className="mt-2">
                  {viewDeal.followUpDate ? (
                    <div className="flex items-center text-yellow-600">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      <span>{new Date(viewDeal.followUpDate).toLocaleDateString()}</span>
                    </div>
                  ) : (
                    <p className="text-gray-500">{t('deals.messages.notSet')}</p>
                  )}
                </div>
              </div>

              {/* Status */}
              <div>
                <h3 className="text-sm font-medium text-gray-600">{t('deals.fields.status')}</h3>
                <div className="mt-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${viewDeal.status === 'completed' ? 'text-green-700 bg-green-100' :
                    viewDeal.status === 'rejected' ? 'text-red-700 bg-red-100' :
                      'text-yellow-700 bg-yellow-100'
                    }`}>
                    {viewDeal.status === 'completed' ? (
                      <CheckIcon className="h-3 w-3 mr-1" />
                    ) : viewDeal.status === 'rejected' ? (
                      <TrashIcon className="h-3 w-3 mr-1" />
                    ) : (
                      <ClockIcon className="h-3 w-3 mr-1" />
                    )}
                    {t(`deals.status.${viewDeal.status}`)}
                  </span>
                </div>
              </div>

              {/* Subject - Only for email */}
              {viewDeal.type === 'email' && viewDeal.subject && (
                <div className="col-span-1 md:col-span-2">
                  <h3 className="text-sm font-medium text-gray-600">{t('deals.fields.subject')}</h3>
                  <p className="mt-2 text-gray-700">{viewDeal.subject}</p>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <h3 className="text-sm font-medium text-gray-600">{t('deals.fields.description')}</h3>
              <p className="mt-2 text-gray-700 whitespace-pre-wrap">{viewDeal.description}</p>
            </div>

            {/* Outcome */}
            {viewDeal.outcome && (
              <div>
                <h3 className="text-sm font-medium text-gray-600">{t('deals.fields.outcome')}</h3>
                <p className="mt-2 text-gray-700 whitespace-pre-wrap">{viewDeal.outcome}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setIsViewModalOpen(false);
                  setSelectedDeal(viewDeal);
                  setIsFormOpen(true);
                }}
                className="px-4 py-2 mr-3 bg-primary-600 hover:bg-primary-500 text-white rounded-md focus:outline-none"
              >
                {t('deals.edit')}
              </button>
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md focus:outline-none"
              >
                {t('common.actions.close')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        onConfirm={confirmDelete}
        title={t('deals.messages.confirmTitle')}
        deleteMessage={`${t('deals.messages.deleteQuestion')}`}
        deleteWarning={t('deals.messages.deleteWarning')}
      />
    </div>
  );
};

export default DealList; 